/**
 * Time pipe
 * Transforms Bcx time object (i.e. { unit,value,type }) into render value (i.e. 10 hours)
 *
 * @version
 * 4.64.0 OAUI-646 Upgrades for Angular v16
 * 4.36.0 OAUI-510 Add / Edit / Remove Attachments
 * 4.35.0 OAUI-500 View order adjustments
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2023 Build Centrix
 */
import { Pipe, PipeTransform } from '@angular/core';
import * as moment from 'moment';

@Pipe({
  name: 'BcxTime',
})
export class BcxTimePipe implements PipeTransform {
  /**
   * Convert float value to hours and minutes
   * @since 4.35.0
   */
  static convertFloatHoursToHoursAndMinutes(hours: number) {
    let result = '0h 00m';

    if (hours > 0) {
      const resultHours = Math.floor(hours);
      const resultMinutes = Math.round((hours - resultHours) * 60);

      // read "2h 06m" instead of "2h 6m"
      const resultMinutesStr =
        resultMinutes < 10 ? '0' + resultMinutes : resultMinutes.toString();
      result =
        resultHours.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        'h ' +
        resultMinutesStr +
        'm';
    }

    return result;
  }

  /**
   * Get Time Format
   * @since 4.35.0
   */
  static getTimeFormat(value: number, unit: string) {
    //convert time to hours
    let msFactor = 1;

    //Convert Milliseconds to Hours
    if (unit === 'millisecond') {
      msFactor = 1 / 3600000;
    }

    // Convert Seconds to Hours
    if (unit === 'second') {
      msFactor = 1 / 3600;
    }

    // Convert Minutes to Hours
    else if (unit === 'minute') {
      msFactor = 1 / 60;
    }

    // Convert Days to Hours
    else if (unit === 'day') {
      msFactor = 24;
    }

    // Convert Weeks to Hours
    else if (unit === 'week') {
      msFactor = 24 * 7;
    }

    return BcxTimePipe.convertFloatHoursToHoursAndMinutes(value * msFactor);
  }

  /**
   * Parse To Human Readable Value
   * @since 4.35.0
   */
  static parseHumanized(value: number, unit: string) {
    let msFactor = 1;

    // Convert Seconds to Milliseconds
    if (unit === 'second') {
      msFactor = 1000;
    }

    // Convert Minutes to Milliseconds
    if (unit === 'minute') {
      msFactor = 1000 * 60;
    }

    // Convert Hours to Milliseconds
    if (unit === 'hour') {
      msFactor = 1000 * 60 * 60;
    }

    // Convert Days to Milliseconds
    if (unit === 'day') {
      msFactor = 1000 * 60 * 60 * 24;
    }

    // Convert Weeks to Milliseconds
    if (unit === 'week') {
      msFactor = 1000 * 60 * 60 * 24 * 7;
    }

    return moment.duration(value * msFactor, 'milliseconds').humanize();
  }

  /**
   * Pipe Transform
   * @since 4.35.0
   */
  transform(obj: any, humanize = false): string {
    let result = '';

    if (humanize) {
      // Negative Result
      if (obj.value < 0) {
        result = '-' + BcxTimePipe.parseHumanized(obj.value, obj.unit);
      }

      // Zero or Positive Result
      if (obj.value > 0 || obj.value === 0) {
        result = BcxTimePipe.parseHumanized(obj.value, obj.unit);
      }
    } else {
      result = BcxTimePipe.getTimeFormat(obj.value, obj.unit);
    }

    return result;
  }
}
